import { array } from 'fp-ts';
import { lt, useFeatureFlag } from '@mews/framework';
import { Card, Stack, Typography } from '@optimus-web/core';
import { UnicodeEntity } from '@mews/enums';
import { ContentSection } from '@mews-commander/ui';
import { FeatureFlag } from '@mews-commander/enums';
import { WithDefaultPolicy } from '../../../types';
import { CancellationPolicyName, SettlementRuleName } from '../../../../../components';

interface DefaultPolicySectionProps {
    rightContent?: React.ReactNode;
    options?: Array<'AutomaticDepositSettlement' | 'CreateSettlementTask' | 'RequirePaymentCardDuringOnlineCheckIn'>;
}

export const DefaultPolicySection = ({
    rightContent,
    defaultPolicy,
    options,
}: DefaultPolicySectionProps & Partial<WithDefaultPolicy>) => {
    const requirePaymentDuringOCI = useFeatureFlag(FeatureFlag.requirePaymentDuringOnlineCheckIn);

    return (
        <ContentSection
            title={lt('RateGroupDefaultPolicySectionTitle')}
            description={lt('RateGroupDefaultPolicySectionDescription')}
            rightContent={rightContent}
        >
            <Card padding="300">
                <Stack spacing="200" vertical>
                    <Stack spacing="50" vertical>
                        <Typography textStyle="bodyMediumStrong" as="div">
                            {lt('Payment')}
                        </Typography>
                        {defaultPolicy && array.isNonEmpty(defaultPolicy.settlementRules)
                            ? defaultPolicy.settlementRules.map((settlementRule) => (
                                  <Typography
                                      key={settlementRule.id}
                                      textStyle="bodyMedium"
                                      variant="secondary"
                                      wordBreak="break-all"
                                      ellipsis
                                      maxLines={1}
                                      as="div"
                                  >
                                      <SettlementRuleName {...settlementRule} format="nameAndParameters" />
                                  </Typography>
                              ))
                            : UnicodeEntity.Dash}
                    </Stack>
                    <Stack spacing="50" vertical>
                        <Typography textStyle="bodyMediumStrong" as="div">
                            {lt('Cancellation')}
                        </Typography>
                        {defaultPolicy && array.isNonEmpty(defaultPolicy.cancellationPolicies)
                            ? defaultPolicy.cancellationPolicies.map((cancellationPolicy) => (
                                  <Typography
                                      key={cancellationPolicy.id}
                                      textStyle="bodyMedium"
                                      variant="secondary"
                                      wordBreak="break-all"
                                      ellipsis
                                      maxLines={1}
                                      as="div"
                                  >
                                      <CancellationPolicyName {...cancellationPolicy} format="nameAndParameters" />
                                  </Typography>
                              ))
                            : UnicodeEntity.Dash}
                    </Stack>
                    {requirePaymentDuringOCI && (
                        <Stack spacing="50" vertical>
                            <Typography textStyle="bodyMediumStrong" as="div">
                                {lt('CheckInRequirePaymentDetails')}
                            </Typography>
                            <Typography textStyle="bodyMedium" variant="secondary" as="div">
                                {options?.includes('RequirePaymentCardDuringOnlineCheckIn') ? lt('Yes') : lt('No')}
                            </Typography>
                        </Stack>
                    )}
                </Stack>
            </Card>
        </ContentSection>
    );
};
